// src/components/ParentCTA.js
import React, { useState, useEffect, useMemo } from 'react';
import { ArrowRight, CheckCircle, Check, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const playfulPrompts = [
  { id: 'boss_baby', text: 'Is your baby the boss at home?', icon: '👑' },
  { id: 'nap_currency', text: 'Are naps your new currency?', icon: '😴' },
  { id: 'coffee_giggles', text: 'Surviving on coffee and baby giggles?', icon: '☕' },
];

// UI-only flow config. The first three steps are generated from playfulPrompts.
const CORE_STEPS = [
  {
    id: 'baby_age',
    label: 'How old is your baby?',
    type: 'number',
    placeholder: 'Enter age in months (0–36)',
    validate: (v) => {
      const n = Number(v);
      return Number.isFinite(n) && n >= 0 && n <= 36;
    },
    error: 'Age must be a number between 0 and 36 months.',
  },
  {
    id: 'readiness',
    label:
      'You’re almost there! Confirm you’ll answer a quick daily check-in (takes <30s).',
    type: 'confirm',
    validate: (v) => v === true,
    error: 'Please confirm to continue.',
  },
];

const buildFlow = () => {
  const playfulSteps = playfulPrompts.map((q) => ({
    id: q.id,
    label: q.text,
    icon: q.icon,
    type: 'scale', // 3-point scale treated as options
    options: ['Yup 🙌', 'Sometimes 🤷‍♀️', 'Nope ❌'],
    appropriate: (choice) => choice === 'Yup 🙌' || choice === 'Sometimes 🤷‍♀️',
    validate: (v) => typeof v === 'string' && v.length > 0,
    error: 'Please choose an option to continue.',
  }));
  return [...playfulSteps, ...CORE_STEPS];
};

const FLOW_STEPS = buildFlow();

const ParentCTA = () => {
  const navigate = useNavigate();

  // Hero entrance animation
  const [isVisible, setIsVisible] = useState(false);

  // Flow state
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [error, setError] = useState('');
  const [completed, setCompleted] = useState(false);

  useEffect(() => setIsVisible(true), []);

  const step = FLOW_STEPS[stepIndex];
  const progress = useMemo(
    () => Math.round((stepIndex / FLOW_STEPS.length) * 100),
    [stepIndex]
  );

  const setAnswer = (id, value) => {
    setAnswers((prev) => ({ ...prev, [id]: value }));
    setError('');
  };

  const onNext = () => {
    const value = answers[step.id];
    if (!step.validate(value)) {
      setError(step.error);
      return;
    }
    if (stepIndex < FLOW_STEPS.length - 1) {
      setStepIndex((i) => i + 1);
    } else {
      // Appropriateness check (UI-only): at least one playful answer is "Yup" or "Sometimes"
      const okCount = playfulPrompts.reduce((acc, q) => {
        const s = FLOW_STEPS.find((t) => t.id === q.id);
        const v = answers[q.id];
        return acc + (s && s.appropriate ? (s.appropriate(v) ? 1 : 0) : 0);
      }, 0);
      if (okCount === 0) {
        setError('Whoops! Spill the beans on at least one—your secrets are safe with us and help us tailor the fun!');
        // Nudge back to first playful step
        setStepIndex(0);
        return;
      }
      setCompleted(true);
    }
  };

  const onBack = () => {
    if (stepIndex === 0) return;
    setError('');
    setStepIndex((i) => i - 1);
  };

  return (
    <section className="py-24 bg-gradient-to-b from-white via-pink-50/20 to-white relative overflow-hidden">
      {/* Subtle background shapes */}
      <div className="absolute inset-0">
        <svg className="absolute top-0 left-0 w-full h-96 opacity-5" viewBox="0 0 1440 384" preserveAspectRatio="none">
          <path d="M0,128 Q360,64 720,128 T1440,128 L1440,0 L0,0 Z" fill="url(#cta-gradient-1)"/>
          <defs>
            <linearGradient id="cta-gradient-1" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#FEC5E5"/>
              <stop offset="100%" stopColor="#C7B5FD"/>
            </linearGradient>
          </defs>
        </svg>

        {/* Subtle floating orbs */}
        <div className="absolute top-1/4 left-10 w-96 h-96 bg-gradient-to-br from-pink-100 to-purple-100 rounded-full mix-blend-multiply filter blur-3xl opacity-5 animate-blob"></div>
        <div className="absolute bottom-1/4 right-10 w-96 h-96 bg-gradient-to-br from-purple-100 to-blue-100 rounded-full mix-blend-multiply filter blur-3xl opacity-5 animate-blob animation-delay-4000"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-4xl mx-auto">
          <div className={`transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            {/* Main heading - LARGE and PROMINENT */}
            <div className="text-center mb-12">
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-[#0C1D40] mb-6">
                Hey there, super parent! <span className="inline-block ml-3 text-5xl">👋</span>
              </h1>
              <p className="text-2xl md:text-3xl text-gray-600 font-light">
                We get it — let’s make this easy.
              </p>
            </div>

            {/* Main content card (interactive flow) */}
            <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl p-10 md:p-16 mt-16 border border-gray-100">
              {/* Welcome to Tifl - PROMINENT */}
              <div className="text-center mb-10">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0C1D40] mb-6 leading-tight">
                  Welcome to <span className="bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent">Tifl</span>
                </h2>

                <div className="flex flex-col items-center gap-3">
                  <p className="text-2xl md:text-3xl text-gray-700">
                    where we turn the <span className="font-bold text-red-500">'what now?'</span> moments into <span className="font-bold text-green-600">'got this!'</span> ones.
                  </p>
                  <p className="text-xl md:text-2xl text-gray-700">
                    Tifl is your little helper for the first <span className="font-bold bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent">1,000 days</span> — milestone tracking, parenting tips, and moments that matter.
                  </p>
                </div>
              </div>

              {/* Progress */}
              {!completed && (
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm md:text-base font-medium text-[#0C1D40]">
                      Step {stepIndex + 1} of {FLOW_STEPS.length}
                    </span>
                    <span className="text-sm md:text-base text-gray-500">{progress}%</span>
                  </div>
                  <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-2 bg-gradient-to-r from-pink-500 to-purple-500 transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Flow Body */}
              {!completed ? (
                <div className="bg-gradient-to-r from-pink-50 via-purple-50/50 to-pink-50 rounded-2xl p-6 md:p-8">
                  {/* Playful question header with icon if present */}
                  <div className="flex items-start gap-3 md:gap-4 mb-6">
                    {step.icon && (
                      <span className="text-4xl md:text-5xl leading-none">{step.icon}</span>
                    )}
                    <h3 className="text-2xl md:text-3xl font-bold text-[#0C1D40]">
                      {step.label}
                    </h3>
                  </div>

                  {/* Scale (playful steps) */}
                  {step.type === 'scale' && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
                      {step.options.map((opt) => {
                        const active = answers[step.id] === opt;
                        return (
                          <button
                            key={opt}
                            type="button"
                            onClick={() => setAnswer(step.id, opt)}
                            className={`w-full text-left px-5 py-4 rounded-xl border transition-all
                              ${active ? 'border-[#0C1D40] bg-white shadow-md' : 'border-gray-200 bg-white/70 hover:bg-white'}
                            `}
                            aria-pressed={active}
                          >
                            <span className="flex items-center justify-between">
                              <span className="text-lg md:text-xl text-gray-800">{opt}</span>
                              {active ? <Check className="w-5 h-5 text-green-600" /> : null}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Single-choice */}
                  {step.type === 'single' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                      {step.options.map((opt) => {
                        const active = answers[step.id] === opt;
                        return (
                          <button
                            key={opt}
                            type="button"
                            onClick={() => setAnswer(step.id, opt)}
                            className={`w-full text-left px-5 py-4 rounded-xl border transition-all
                              ${active ? 'border-[#0C1D40] bg-white shadow-md' : 'border-gray-200 bg-white/70 hover:bg-white'}
                            `}
                            aria-pressed={active}
                          >
                            <span className="flex items-center justify-between">
                              <span className="text-lg md:text-xl text-gray-800">{opt}</span>
                              {active ? <Check className="w-5 h-5 text-green-600" /> : null}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Number input */}
                  {step.type === 'number' && (
                    <div className="flex items-center gap-3">
                      <input
                        type="number"
                        min="0"
                        max="36"
                        inputMode="numeric"
                        value={answers[step.id] ?? ''}
                        onChange={(e) => setAnswer(step.id, e.target.value)}
                        placeholder={step.placeholder}
                        className="w-full md:w-1/2 px-5 py-4 rounded-xl border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 text-lg"
                      />
                    </div>
                  )}

                  {/* Confirm */}
                  {step.type === 'confirm' && (
                    <label className="flex items-start gap-3 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={Boolean(answers[step.id])}
                        onChange={(e) => setAnswer(step.id, e.target.checked)}
                        className="mt-1 w-5 h-5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="text-lg md:text-xl text-gray-800">
                        I’m ready for quick daily check-ins so Tifl can personalize my journey.
                      </span>
                    </label>
                  )}

                  {/* Error */}
                  {error && (
                    <div className="mt-5 flex items-center gap-2 text-red-600">
                      <X className="w-5 h-5" />
                      <span className="text-sm md:text-base">{error}</span>
                    </div>
                  )}

                  {/* Nav Buttons */}
                  <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                    <button
                      type="button"
                      onClick={onNext}
                      className="group flex-1 sm:flex-none bg-gradient-to-r from-[#0C1D40] to-[#1A2B50] text-white px-8 py-4 rounded-full hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02]"
                    >
                      <span className="flex items-center justify-center gap-2 text-lg md:text-xl font-bold">
                        {stepIndex < FLOW_STEPS.length - 1 ? 'Continue' : 'Finish'}
                        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </button>

                    <button
                      type="button"
                      onClick={onBack}
                      disabled={stepIndex === 0}
                      className={`px-8 py-4 rounded-full border transition-all ${
                        stepIndex === 0
                          ? 'border-gray-200 text-gray-300 cursor-not-allowed'
                          : 'border-gray-300 text-gray-700 hover:bg-white'
                      }`}
                    >
                      Back
                    </button>
                  </div>
                </div>
              ) : (
                // Success Gate → Prompt Sign In
                <div className="text-center">
                  <div className="mx-auto w-20 h-20 rounded-full bg-green-50 flex items-center justify-center">
                    <CheckCircle className="w-12 h-12 text-green-500" />
                  </div>
                  <h3 className="mt-6 text-3xl md:text-4xl font-bold text-[#0C1D40]">
                    You’re all set! 🎉
                  </h3>
                  <p className="mt-3 text-lg md:text-xl text-gray-700">
                    Based on your answers, Tifl will personalize milestone tracking and daily tips.
                  </p>

                  <div className="mt-8">
                    <button
                      onClick={() => navigate('/signin')}
                      className="group bg-gradient-to-r from-[#0C1D40] to-[#1A2B50] text-white px-14 py-6 rounded-full hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
                    >
                      <span className="flex items-center gap-3 text-xl md:text-2xl font-bold">
                        Sign in to start your 1,000-day journey
                        <ArrowRight className="w-6 h-6 group-hover:translate-x-2 transition-transform" />
                      </span>
                    </button>

                    {/* Trust indicators */}
                    <div className="flex flex-wrap justify-center items-center gap-6 mt-8 text-base text-gray-600">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-500" />
                        <span>Free to start</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-500" />
                        <span>No credit card required</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-500" />
                        <span>Join 50,000+ parents</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Keep the closing line below the card for continuity */}
            {!completed && (
              <div className="text-center mt-8">
                <p className="text-3xl md:text-4xl font-bold text-[#0C1D40]">
                  Wanna make parenting a little
                  <span className="bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent"> easier</span>
                  <span className="text-[#0C1D40]"> and more </span>
                  <span className="bg-gradient-to-r from-purple-500 to-indigo-500 bg-clip-text text-transparent">fun</span>?
                </p>
              </div>
            )}

          </div>
        </div>
      </div>
    </section>
  );
};

export default ParentCTA;
